import { Divider } from "$root/components/Divider";
import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import { useLocaleFormatters } from "$root/localization/hooks";
import { Highcharts } from "$root/third-party-integrations/highcharts-with-modules";
import { ActionText, StackingContext, Text } from "@mdotm/shadcn/legacy/components";
import { useDraggable } from "@mdotm/shadcn/legacy/headless";
import { useDrivenState, useRefProxy, useUniqueDOMId, type NodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { clamp, isBetween } from "@mdotm/shadcn/legacy/utils";
import HighchartsReact, { type HighchartsReactRefObject } from "highcharts-react-official";
import { Set } from "immutable";
import {
	Fragment,
	type ForwardedRef,
	useContext,
	useEffectEvent,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { renderToString } from "react-dom/server";
import { categoricalChartColor, dimmedSourceOpacity, othersChartColor } from "./categorical-palette";
import { applyBandSelection, handleSelectionClick, itemIdsInIndexRange } from "./chart-selection";
import { calculatePointWidth } from "./point-width";
import {
	ChartCardShell,
	ChartTitle,
	chartHeightInPx,
	ChartWallSwitch,
	navigatorSourceClassName,
	type ChartHeight,
	type ChartWallStatus,
} from "./ChartCardShell";

/**
 * Stacked column: portfolios on the X axis, the charted metric split into one stacked segment per
 * breakdown category (e.g. currency exposure → one segment per currency).
 *
 * CURRENT and PROPOSAL survive as two *stacks per category* — Highcharts does that natively, no extra
 * module — and `highlightSecondaryMetric` dims the CURRENT stack, the same overlay convention the column
 * chart uses. There is no threshold: the Y axis is a *sum over categories*, and a single-metric threshold
 * drawn across a stack would compare against a total that is not the same quantity.
 *
 * There *is* a navigator (FR-7.1): every selected portfolio is always drawn, so a large selection is made
 * navigable by the strip at the bottom rather than by dropping rows. Same X axis as `MetricComparisonChart`,
 * and the same config bar the strip's own series: a column per portfolio of the stack total, rather than
 * Highcharts' `areaspline` through the base series. Where the column chart's plot and its strip are the same
 * marks already, here they have to be made so — through a hidden series the strip is told to read, which is
 * the whole of the difference between the two configs and is also what keeps the strip *interactive* across
 * re-renders; see `stackTotals` for both halves of that. It costs no constructor change — the chart renders
 * through plain `HighchartsReact` and the stock module is registered globally (`highcharts-with-modules`).
 *
 * Ordering is the caller's: the rows are drawn in the order they are given. A breakdown concept declares
 * zero comparison operators and `sortable = false`, so the backend's comparator cannot order one — the
 * workspace reorders the fetched page instead (`sortStackedChartData`).
 *
 * Selection is the column chart's, through the same `chart-selection` helpers and the same workspace-level
 * selection: click to toggle a portfolio, shift-click to extend to a range, drag a band to take what it
 * spans, shift-drag to toggle it. Only the band's x extent is read — a stack is one portfolio from bottom
 * to top, so a vertical extent would mean nothing here.
 *
 * What cannot be shared is how a selected column *looks*: the column chart recolours it, and here colour
 * is the category encoding, so the unselected columns are greyed out instead (`unselectedSegmentAttribs`).
 * Opacity is left to "Highlight proposal", which dims the CURRENT stack with it (`opacityFor`).
 */

export type StackedMetricChartItem = {
	id: string;
	name: string;
	/**
	 * `[CURRENT, PROPOSAL]`, each a value per breakdown category positionally matching
	 * {@link StackedMetricChartProps.categories}. `null` for PROPOSAL means the portfolio has none.
	 */
	values: [Array<number | null>, Array<number | null> | null];
};

export type StackedMetricChartProps = {
	innerRef?: ForwardedRef<HighchartsReactRefObject>;
	chartTitle: string;
	mainMetricLabel: string;
	secondaryMetricLabel: string;
	/**
	 * Stable, response-level order. Drives both legend order and colour assignment.
	 *
	 * Referentially stable too, as `data` must be: the chart's Highcharts options are memoized on the identity
	 * of both, and a fresh array per render puts the chart through a full `chart.update(…, oneToOne: true)` —
	 * every series re-inited — on every render of whatever holds it. Pass them out of a `useMemo`, never as a
	 * literal in the JSX.
	 */
	categories: Array<string>;
	data: Array<StackedMetricChartItem>;
	/** @default false */
	hasProposal?: boolean;
	/** Dims the {@link mainMetricLabel} stack so the proposal stack reads as the primary one. */
	highlightSecondaryMetric?: boolean;
	/** @default true */
	showLegend?: boolean;
	/** e.g. % */
	unit?: string;
	midActions?: NodeOrFn;
	actions?: NodeOrFn;
	/** @default 'tall' */
	chartHeight?: ChartHeight;
	selectedIds?: Set<string> | null;
	onSelectionChange?(newSelection: Set<string>): void;
	/** Suspends the band drag while another surface owns the pointer (an open side panel, a non-chart view). */
	disable?: boolean;
	tooltipTitleHrefProvider?(itemId: string): string;
};

/**
 * How an unselected segment is drawn *while a selection exists* — with nothing selected there is no "rest"
 * to push back, so every column keeps its category colours.
 *
 * Greyed at full opacity rather than dimmed, so the columns neither fade into the card nor mix colours where
 * the two stacks overlap. The column chart recolours the *selected* bar instead, which a stacked column has
 * no room for: its fills are the categories. One neutral would merge a stack into a single block, so the
 * greyed segments keep a hairline in the card's background colour (`plotOptions.column.borderColor`).
 */
const unselectedSegmentAttribs = {
	color: themeCSSVars.palette_N200,
	borderWidth: 1,
} satisfies Highcharts.PointOptionsObject;

/** Highcharts honours `opacity` on a point in `pointAttribs`, but leaves it out of `PointOptionsObject`. */
type PointOptionsWithOpacity = Highcharts.PointOptionsObject & { opacity?: number };

/**
 * The `distance` a tooltip is placed at is resolved per hovered series — a column's default is not the chart
 * level one — and kept on the live tooltip, where `Tooltip`'s type leaves it out.
 */
type TooltipWithDistance = Highcharts.Tooltip & { distance: number };

/** The rect a column point was drawn as, in plot coordinates; on the live point, and absent from `Point`. */
type PointWithShape = Highcharts.Point & { shapeArgs?: { x?: number; width?: number } };

/** The label the backend uses for the aggregated tail; drawn neutral so it does not read as a category. */
const othersLabel = "Other";

/**
 * Name of the hidden series the navigator strip is built from — see `stackTotals`. Never rendered anywhere a
 * reader can see it (no plot, no legend, no tooltip), so it is an internal handle rather than a label.
 */
const navigatorSourceSeriesName = "__navigator-stack-totals";

/**
 * The `id` every series carries, one per role, so that `chart.update` matches the incoming options to the
 * live series **by identity rather than by position**.
 *
 * Highcharts' React wrapper updates through `chart.update(options, redraw, oneToOne = true)`, and that
 * matches each collection one to one by *array index*, falling back on the index only when a series declares
 * no `id`. `Series#update` then merges the incoming options *over the ones the series already had*, so every
 * option the new options do not mention survives. Together those two are a trap for a series list whose
 * length is the caller's data: the breakdown's top-N is steppable, a refetch can come back with a different
 * tail, and `hasProposal` turns one series per category into two. Any of those shifts the list, and the
 * series in the shifted slot inherits its predecessor's leftovers.
 *
 * What leaks is exactly what the reader then reports as missing: the navigator source's `visible: false`,
 * `showInLegend: false` and `className` land on a *category*, which vanishes from the plot **and** from the
 * legend while the strip — a picture of `stackTotals`, computed from `props.data` and so still complete —
 * keeps showing the column the plot no longer draws. `linkedTo` leaks the same way, taking a second category
 * out of the legend with it.
 *
 * Keyed on the category's *position* rather than on its name: names are the backend's, so two equal labels
 * would collapse two series onto one id, and a slot swapping which category it draws costs nothing — every
 * option a category series carries is spelled out on every render.
 */
const seriesIdFor = (source: 0 | 1, categoryIndex: number) =>
	`${source === 0 ? "current" : "proposal"}-${categoryIndex}`;

/** @see seriesIdFor */
const navigatorSourceSeriesId = "navigator-source";

/**
 * How many portfolios can be on screen before their names stop fitting under the axis.
 *
 * Read against the *visible* count rather than `props.data.length` since the navigator arrived: the axis is
 * zoomable now, so a 200-portfolio chart that the user has scrolled down to a dozen columns has all the
 * room in the world for labels, and hiding them because the *selection* is large would be answering a
 * question nobody asked. Zoomed out it still hides them, which is the behaviour that was there before.
 */
const maxLabelledPortfolios = 20;

/** The sum of a stack's present segments; `null` where it has none, so an empty stack reads as a gap. */
const stackTotalOf = (values: Array<number | null> | null): number | null => {
	const present = (values ?? []).filter((value): value is number => value !== null);
	return present.length > 0 ? present.reduce((total, value) => total + value, 0) : null;
};

/** Pixels between the hovered column group and the tooltip standing beside it. */
const tooltipGap = 8;

export function StackedMetricChart(props: StackedMetricChartProps): React.JSX.Element {
	const highchartsRef = useRefProxy(null, props.innerRef);
	const unit = props.unit ?? "";
	const { formatNumber } = useLocaleFormatters();
	const hasProposal = props.hasProposal ?? false;
	const chartId = useUniqueDOMId();
	const tooltipId = useUniqueDOMId();
	// The layer the app stacks its floating surfaces at, for the tooltip below — see `outside`.
	const { zIndex } = useContext(StackingContext);

	const [selectedIds, setSelectedIds] = useDrivenState(props.selectedIds ?? Set<string>(), {
		equalityComparator: (a, b) => a.equals(b),
	});
	const [band, setBand] = useState<{ left: number; width: number; top: number; height: number } | null>(null);
	const dragStateRef = useRef<{
		/** Shift keeps what was already selected and toggles the covered portfolios against it. */
		subType: "toggle" | "new";
		initialSelection: Set<string>;
		currentSelection: Set<string>;
	} | null>(null);
	/** Where a shift-click extends from — the last click, or the trailing edge of the last band. */
	const shiftSelectionIdRef = useRef<string | null>(null);
	/** How many portfolios the navigator's window currently spans; drives the axis labels. */
	const [visibleCount, setVisibleCount] = useState(props.data.length);

	const { dragging } = useDraggable({
		draggableEl: props.disable ? undefined : window,
		filter(initialCoords, keyModifiers) {
			// A press on the tooltip is a click on its link, not the start of a band: `tooltip.outside` stands it
			// beside the hovered column, which is over that column's neighbours.
			const tooltipBox = document.getElementById(tooltipId)?.getBoundingClientRect();
			if (
				tooltipBox &&
				isBetween(initialCoords.x, tooltipBox.left, tooltipBox.right) &&
				isBetween(initialCoords.y, tooltipBox.top, tooltipBox.bottom)
			) {
				return false;
			}
			const cardAreaEl = document.getElementById(chartId);
			const container = cardAreaEl?.querySelector(".highcharts-container");
			if (!container) {
				return false;
			}
			const box = container.getBoundingClientRect();
			// The navigator strip is a control, not plot: a drag that starts on it is Highcharts' own
			// pan/zoom, and letting the rubber band claim it would both select portfolios the user never
			// meant and fight the handle for the pointer. So the drag region stops at the navigator's top
			// edge — the same guard `MetricComparisonChart` applies for the same reason. Falls back to the
			// container's bottom before the navigator has rendered (or where it cannot, e.g. jsdom).
			const navigatorTop = cardAreaEl?.querySelector(".highcharts-navigator")?.getBoundingClientRect().top;
			const dragBottom = navigatorTop ?? box.bottom;
			if (!isBetween(initialCoords.x, box.left, box.right) || !isBetween(initialCoords.y, box.top, dragBottom)) {
				return false;
			}
			dragStateRef.current = {
				subType: keyModifiers.shift ? "toggle" : "new",
				initialSelection: selectedIds,
				currentSelection: selectedIds,
			};
			return true;
		},
		preventDefault: ({ phase }) => phase !== "begin",
		stopPropagation: ({ phase }) => phase !== "begin",
		onDragMove(initial, current) {
			const chart = highchartsRef.current?.chart;
			if (!chart || !dragStateRef.current) {
				return;
			}
			const box = chart.container.getBoundingClientRect();
			// 1-D: only the x extent of the drag matters, a stack being one portfolio bottom to top.
			const [fromPx, toPx] = [Math.min(initial.x, current.x) - box.left, Math.max(initial.x, current.x) - box.left];
			if (toPx - fromPx < 2) {
				return;
			}

			setBand({
				left: box.left + clamp(fromPx, chart.plotLeft, chart.plotLeft + chart.plotWidth),
				width:
					clamp(toPx, chart.plotLeft, chart.plotLeft + chart.plotWidth) -
					clamp(fromPx, chart.plotLeft, chart.plotLeft + chart.plotWidth),
				top: box.top + chart.plotTop,
				height: chart.plotHeight,
			});

			// Categorical axis: `toValue` gives a fractional category index, which rounds to the portfolio
			// whose stack the pointer is over.
			const axis = chart.xAxis[0];
			const spannedIds = itemIdsInIndexRange(
				props.data,
				Math.round(axis.toValue(fromPx)),
				Math.round(axis.toValue(toPx)),
			);
			if (spannedIds.length > 0) {
				// The edge the drag ended on, so a following shift-click extends the band rather than restarting.
				shiftSelectionIdRef.current = current.x - initial.x > 0 ? spannedIds[0] : spannedIds[spannedIds.length - 1];
			}

			dragStateRef.current.currentSelection = applyBandSelection(
				dragStateRef.current.initialSelection,
				Set(spannedIds),
				dragStateRef.current.subType,
			);
			setSelectedIds(dragStateRef.current.currentSelection);
		},
		onDragEnd() {
			if (
				dragStateRef.current &&
				!dragStateRef.current.currentSelection.equals(dragStateRef.current.initialSelection)
			) {
				props.onSelectionChange?.(dragStateRef.current.currentSelection);
			}
			dragStateRef.current = null;
			setBand(null);
		},
	});

	/**
	 * The two props Highcharts reaches through a *callback* rather than through a value, at an identity that
	 * does not change — which is what lets `chartOptions` below depend on the props it reads instead of on
	 * `props` wholesale.
	 *
	 * Neither is stable at the call site, and cannot be asked to be: `onSelectionChange={(s) => …}` written
	 * inline is a new function on every render of whatever holds the chart. As a dependency, either one costs
	 * a full `chart.update(…, oneToOne: true)` per render — see the note on the memo's dependencies.
	 *
	 * `useEffectEvent` results are stable by construction, which is why they are absent from that list.
	 */
	const emitSelectionChange = useEffectEvent((newSelection: Set<string>) => props.onSelectionChange?.(newSelection));
	const tooltipTitleHrefOf = useEffectEvent((itemId: string) => props.tooltipTitleHrefProvider?.(itemId));

	/**
	 * Memoized for the same reason the options are, and just as necessary: `containerProps` is the *other*
	 * entry in the dependency list of the layout effect that calls `chart.update`, so an object literal here
	 * re-inits every series on every render no matter how stable the options object is.
	 */
	const containerProps = useMemo(
		() => ({
			style: { height: chartHeightInPx(props.chartHeight ?? "tall"), width: "100%" },
			className: "cursor-crosshair",
		}),
		[props.chartHeight],
	);

	const chartOptions = useMemo((): Highcharts.Options => {
		const colorFor = (category: string, index: number) =>
			category === othersLabel ? othersChartColor : categoricalChartColor(index);

		// Nothing to highlight *against* without a second stack, so the dimming waits for one.
		const dimCurrent = (props.highlightSecondaryMetric ?? false) && hasProposal;

		// A selection reads as "these portfolios", not "this segment", so it is drawn on the points of every
		// series at once: the columns of the portfolios left out are greyed, whole stack included, in both
		// stacks.
		const isUnselected = (item: StackedMetricChartItem) => selectedIds.size > 0 && !selectedIds.has(item.id);

		// Resolved into each point's own `opacity` rather than set on the series: Highcharts applies a
		// series-level `opacity` to the plot group *and* to every point it contains, so 0.4 would land twice.
		const opacityFor = (source: 0 | 1) => (dimCurrent && source === 0 ? dimmedSourceOpacity : 1);

		// Every point spells out its colour and border, selected or not: `chart.update` merges a point's new
		// options over its old ones, so a key left out would keep the grey once the portfolio is selected again.
		const pointsFor = (source: 0 | 1, categoryIndex: number, categoryColor: string) =>
			props.data.map(
				(item): PointOptionsWithOpacity => ({
					y: (source === 0 ? item.values[0] : item.values[1])?.[categoryIndex] ?? null,
					opacity: opacityFor(source),
					...(isUnselected(item) ? unselectedSegmentAttribs : { color: categoryColor, borderWidth: 0 }),
				}),
			);

		const selectionEvents: Highcharts.SeriesEventsOptionsObject = {
			click: handleSelectionClick(
				(newSelection) => {
					setSelectedIds(newSelection);
					emitSelectionChange(newSelection);
				},
				{ data: props.data, selectedIds, shiftSelectionIdRef },
			),
		};

		const series = props.categories.flatMap((category, categoryIndex): Array<Highcharts.SeriesColumnOptions> => {
			const color = colorFor(category, categoryIndex);
			const current: Highcharts.SeriesColumnOptions = {
				type: "column",
				id: seriesIdFor(0, categoryIndex),
				name: category,
				color,
				stack: props.mainMetricLabel,
				pointWidth: calculatePointWidth({
					visibleCount,
					metricType: "main",
					metricState: "normal",
					hasSecondaryMetric: hasProposal,
				}),
				data: pointsFor(0, categoryIndex, color),
				events: selectionEvents,
				// The strip reads `navigatorSourceSeriesName`, never a category — see `stackTotals`.
				showInNavigator: false,
			};

			if (!hasProposal) {
				return [current];
			}

			return [
				current,
				{
					type: "column",
					id: seriesIdFor(1, categoryIndex),
					name: category,
					color,
					stack: props.secondaryMetricLabel,
					// One legend entry per breakdown category, not per category × source: the two stacks are
					// the same categories seen twice.
					//
					// Linked by the CURRENT stack's `id` and not by `":previous"`, which Highcharts resolves
					// against the *live* `chart.series` order: a series added by `chart.update` is appended,
					// so a category that arrives after the first render sits behind the navigator source in
					// that array, and a `":previous"` proposal stack would link itself to the hidden series
					// and disappear along with it. An id says which stack it belongs to at any order.
					linkedTo: seriesIdFor(0, categoryIndex),
					// Narrower than the current stack, which is how the column chart marks the proposal too.
					pointWidth: calculatePointWidth({
						visibleCount,
						metricType: "secondary",
						metricState: "normal",
					}),
					data: pointsFor(1, categoryIndex, color),
					events: selectionEvents,
					showInNavigator: false,
				},
			];
		});

		// The navigator's silhouette: the quantity the Y axis actually plots — the stack total — one *column* per
		// portfolio, which is the chart in miniature.
		//
		// Two separate things go wrong when Highcharts builds the strip itself. It reads the *base* series, which
		// here is one breakdown category out of many, so it drew "the first segment of every stack": a reading of
		// the data the chart never charted. And whatever it reads it draws as an `areaspline` with markers off —
		// which interpolates across the portfolios in between, so the strip curves through values nothing was ever
		// plotted at, and which renders a portfolio whose neighbours are both empty as a one-point segment, i.e. a
		// zero-width area: an isolated column is in the chart and *missing from the strip*. Giving the strip data
		// fixes only the first of the two, hence the type as well.
		//
		// It is carried by a series of the *chart*, hidden in the plot and flagged `showInNavigator`, rather than
		// handed to the strip as `navigator.series.data`. The strip looks the same either way; what differs is
		// what `chart.update()` then does to the navigator, and `HighchartsReact` calls `chart.update()` after
		// every render. Highcharts diffs the incoming options against the live ones through `cleanRecursively`,
		// which treats *any* array as changed — it never compares element by element — so a `data` array under
		// `navigator` makes the navigator options read as dirty on every single render. `Chart.update` routes a
		// dirty `navigator` to `Navigator.update`, which patches nothing: it destroys the navigator and re-inits
		// it. The rebuilt strip re-adds its group to the renderer mid-update and lands *behind* the chart
		// background, where it stops receiving pointer events altogether — the first drag works and from then on
		// the strip is a picture: no pan, no second zoom. Keeping every value under `navigator` a primitive leaves
		// those options clean, so the live navigator — masks, handles and drag state — survives the render, and
		// `Series#afterUpdate` re-syncs the strip from this series anyway.
		//
		// The taller of the two stacks where there is a proposal, so a portfolio only the proposal gives a column
		// to still gets one here; never whichever stack is currently highlighted — the strip is a map of the whole
		// selection, and having it redraw itself when "Highlight proposal" is toggled would make it read as a
		// second plot rather than as a control.
		//
		// `null` for a portfolio with no value in either stack, so it reads as a gap rather than as a total that
		// happens to be zero — a column series leaves that slot empty and its neighbours untouched. The strip
		// keeps one point per portfolio either way: the navigator's own x axis takes its `pointRange` from this
		// series, and that half-category is what the window edges are measured against, so a filtered-down data
		// array would shift the handles off the columns.
		const stackTotals = props.data.map((item) => {
			const totals = [stackTotalOf(item.values[0]), hasProposal ? stackTotalOf(item.values[1]) : null].filter(
				(total): total is number => total !== null,
			);
			return totals.length > 0 ? Math.max(...totals) : null;
		});
		/** The totals that reach the strip as a column, i.e. what its y axis has to cover. */
		const plottedTotals = stackTotals.filter((total): total is number => total !== null);
		series.push({
			type: "column",
			id: navigatorSourceSeriesId,
			name: navigatorSourceSeriesName,
			className: navigatorSourceClassName,
			data: stackTotals,
			showInNavigator: true,
			// Drawn in the strip only. `visible: false` keeps it out of the plot, out of the Y axis' extremes and
			// out of Highcharts' column metrics — so the real columns keep exactly the width and offset they have
			// without it — while `navigator.series.visible` puts the strip's copy of it back on. Out of the legend
			// and untrackable by the pointer, so it stays invisible to the reader in every other sense too.
			visible: false,
			showInLegend: false,
			enableMouseTracking: false,
		});

		return {
			chart: {
				type: "column",
				style: { fontFamily: "Gotham,sans-serif" },
			},
			title: { text: "" },
			credits: { enabled: false },
			legend: {
				enabled: props.showLegend ?? true,
				align: "center",
				verticalAlign: "bottom",
			},
			xAxis: {
				categories: props.data.map((item) => item.name),
				labels: { enabled: visibleCount <= maxLabelledPortfolios },
				tickWidth: 0,
				events: {
					afterSetExtremes(event) {
						if (event.min != null && event.max != null) {
							const minIndex = Math.max(0, Math.floor(event.min));
							const maxIndex = Math.min(props.data.length - 1, Math.ceil(event.max));
							setVisibleCount(maxIndex + 1 - minIndex);
						}
					},
				},
			},
			yAxis: {
				gridLineDashStyle: "Dash",
				title: { text: "" },
				labels: {
					formatter(ctx) {
						return `${formatNumber(ctx.value, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}${unit}`;
					},
				},
			},
			plotOptions: {
				column: {
					stacking: "normal",
					borderWidth: 0,
					// Drawn only where a point sets a width of its own: the greyed segments' hairline.
					borderColor: themeCSSVars.palette_N0,
					animation: { duration: 0 },
					shadow: false,
				},
			},
			tooltip: {
				enabled: !dragging,
				useHTML: true,
				padding: 0,
				borderWidth: 0,
				shadow: true,
				stickOnContact: true,
				followPointer: false,
				// Drawn into `document.body` rather than clipped to the chart's SVG, so it can stand *beside* the
				// hovered column: the stacks all sum to the same total, so any spot inside the plot covers a bar.
				outside: true,
				// A body-level element is stacked against the page, not the chart: Highcharts gives it `z-index: 3`,
				// and the workspace draws its active view at a higher layer, which paints the chart over its own
				// tooltip. So it takes the layer the app's floating surfaces use — the band overlay's, below.
				style: { zIndex },
				// Beside the hovered column group, top-aligned with the plot, and flipped to the group's left when
				// the right side would leave the viewport. `outside` positions are page coordinates, and the label
				// sits `distance` pixels inside the container they place, hence the final offset.
				//
				// The group is measured from the columns actually drawn at the hovered index, not from the category:
				// a category holds a slot for each stack whether or not the portfolio fills it, and with a handful
				// of portfolios that slot is hundreds of pixels wide, so the category's edge can be nowhere near
				// the bar the reader is pointing at.
				positioner(this, tooltipWidth, tooltipHeight) {
					const chart = this.chart;
					const index = chart.hoverPoint?.index ?? 0;
					const origin = chart.pointer.getChartPosition();
					const drawn = chart.series.flatMap((series): Array<[number, number]> => {
						// Plot columns only: the navigator's copy of the strip series is a visible series of the chart too,
						// laid out against its own axis, and a rect there is nowhere near the hovered bar. A null point — the
						// proposal slot of a portfolio without one — is laid out as well, so it is excluded by hand: its rect
						// is where the bar *would* be, and nothing is drawn there.
						const inPlot = series.visible && series.xAxis === chart.xAxis[0];
						const point = inPlot ? series.points.find((point) => point.index === index) : undefined;
						const shape = point?.y != null ? (point as PointWithShape).shapeArgs : undefined;
						return typeof shape?.x === "number" && typeof shape.width === "number"
							? [[shape.x, shape.x + shape.width]]
							: [];
					});
					const plotLeft = origin.left + chart.plotLeft;
					const groupLeft = plotLeft + Math.min(...drawn.map(([left]) => left));
					const groupRight = plotLeft + Math.max(...drawn.map(([, right]) => right));
					const viewportRight = window.scrollX + document.documentElement.clientWidth;
					const viewportBottom = window.scrollY + document.documentElement.clientHeight;
					// Flipped only when the left side has the room the right side lacks; with neither, the right side
					// keeps the tooltip on the column it belongs to rather than off the page's left edge.
					const fitsRight = groupRight + tooltipGap + tooltipWidth <= viewportRight;
					const fitsLeft = groupLeft - tooltipGap - tooltipWidth >= window.scrollX;
					const x = fitsRight || !fitsLeft ? groupRight + tooltipGap : groupLeft - tooltipGap - tooltipWidth;
					const y = clamp(
						origin.top + chart.plotTop,
						window.scrollY,
						Math.max(window.scrollY, viewportBottom - tooltipHeight),
					);
					const { distance } = this as TooltipWithDistance;
					return { x: x - distance, y: y - distance };
				},
				// One tooltip per portfolio, from whichever segment the pointer is on: the body is read off `props.data`
				// at the point's index, never off the point itself. Not `shared` — that would replace the column's
				// hover-by-touch with nearest-by-x, and light the tooltip up from empty plot.
				formatter(this) {
					const hovered = this.point;
					const item = hovered ? props.data[hovered.index] : undefined;
					if (!hovered || !item) {
						return "";
					}
					const chart = hovered.series.chart;
					// Resolved once, and branched on rather than on the provider's presence: `tooltipTitleHrefOf` is
					// defined whether or not the caller passed a provider, so the href it returns is the only thing that
					// says whether the title is a link.
					const titleHref = tooltipTitleHrefOf(item.id);
					const format = (value: number | null) =>
						value === null ? "N/A" : `${formatNumber(value, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}${unit}`;
					// A category the legend has switched off is out of the stack, so it is out of the rows and of the
					// totals too — the same reading `stackTotal` gives.
					const shownCategories = props.categories.flatMap((category, categoryIndex) =>
						chart.series.find((series) => series.options.id === seriesIdFor(0, categoryIndex))?.visible === false
							? []
							: [{ category, categoryIndex }],
					);
					const valuesOf = (source: 0 | 1) =>
						shownCategories.map(({ categoryIndex }) => item.values[source]?.[categoryIndex] ?? null);
					const [currentValues, proposalValues] = [valuesOf(0), valuesOf(1)];
					const cell = (value: number | null) => (
						<Text type="Body/M/Bold" className="text-right">
							{format(value)}
						</Text>
					);

					return renderToString(
						<div
							className="p-2 rounded-sm bg-white min-w-60 shadow-sm pointer-events-auto flex flex-col gap-2"
							id={tooltipId}
						>
							<div className="py-1 rounded-sm bg-[#EFF0F3] text-center">
								{titleHref != null ? (
									<ActionText href={titleHref} type="Body/M/Bold">
										{item.name}
									</ActionText>
								) : (
									<Text type="Body/M/Bold">{item.name}</Text>
								)}
							</div>
							<div
								className={`grid items-center gap-x-4 gap-y-1 ${
									hasProposal ? "grid-cols-[1fr_auto_auto]" : "grid-cols-[1fr_auto]"
								}`}
							>
								{hasProposal && (
									<>
										<span />
										<Text type="Body/S/Book" className="text-right">
											{props.mainMetricLabel}
										</Text>
										<Text type="Body/S/Book" className="text-right">
											{props.secondaryMetricLabel}
										</Text>
									</>
								)}
								{shownCategories.map(({ category, categoryIndex }, row) => (
									<Fragment key={categoryIndex}>
										<div className="flex items-center gap-1">
											<ColoredRectangle variant="vertical" color={colorFor(category, categoryIndex)} />
											<Text type="Body/M/Book">{category}</Text>
										</div>
										{cell(currentValues[row])}
										{hasProposal && cell(proposalValues[row])}
									</Fragment>
								))}
								<Divider className="col-span-full" />
								<Text type="Body/S/Book">Total</Text>
								{cell(stackTotalOf(currentValues))}
								{hasProposal && cell(stackTotalOf(proposalValues))}
							</div>
						</div>,
					);
				},
			},
			series,
			// FR-7.1: every selected portfolio is always drawn, so a large selection stays navigable through
			// the scrollbar rather than by dropping rows. Same X axis, same config as `MetricComparisonChart`.
			navigator: {
				enabled: true,
				height: 30,
				// Primitives only, deliberately: an array here — a `series.data` above all — makes Highcharts
				// destroy and rebuild the navigator on every render, which leaves it inert. See `stackTotals`.
				series: {
					// One mark per portfolio at the height the chart draws it, rather than the default
					// `areaspline` through the same points — see `stackTotals` for what that smoothing costs.
					// Columns also give the strip's x axis a `pointRange` of one category, the main axis' own, so
					// the window edges land on column boundaries instead of on the first and last centres.
					type: "column",
					color: themeCSSVars.palette_S300,
					opacity: 0.7,
					// The strip inherits its source series' visibility, and that one is hidden in the plot on
					// purpose; the strip is the one place it *is* meant to be drawn.
					visible: true,
					// The default averages neighbouring points into one mark once they outnumber half the strip's
					// pixels, in *time* units, which on an axis of portfolios is not a grouping of anything.
					dataGrouping: { enabled: false },
					// Where a column starts. The navigator's own default is `null` — "no threshold", i.e. start at
					// the bottom of the strip — which is right for the areaspline silhouette it expects and wrong for
					// a bar meant to be read as a quantity: it makes a mark's height the total's distance from
					// whatever the lowest total happens to be, so the axis bound below is only half the answer.
					threshold: 0,
				},
				xAxis: {
					labels: {
						enabled: false,
					},
				},
				yAxis: {
					labels: {
						enabled: false,
					},
					// The other half of reading a column as a quantity: the strip's y axis has to *contain* the
					// `threshold` above, and it auto-scales like any other axis, so a selection whose stacks all sum
					// to 100% gets an axis of [99.5, 100.5] — zero nowhere near it, every column clamped to the
					// strip's bottom edge, and a hundredth of a percent between two portfolios drawn as half a
					// strip. The marks then contradict the plot they are a picture of, where those two columns are
					// the same height. Bounding the axis at zero makes a mark's height proportional to the total it
					// stands for: equal totals, equal columns.
					//
					// A bound rather than the min itself, because a hedged leg's total is negative and an axis that
					// began at zero would leave its column no room to hang in. The far end is pinned for the mirror
					// case — all-negative totals, where an auto max stops below zero — and costs only the 10%
					// headroom the default padding would have left above the tallest column.
					//
					// Numbers, so the navigator options stay free of arrays and the live strip survives the render
					// — see `stackTotals`. `undefined` where nothing is plotted, since the two would otherwise
					// collapse onto each other into a zero-range axis.
					min: plottedTotals.length > 0 ? Math.min(0, ...plottedTotals) : undefined,
					max: plottedTotals.length > 0 ? Math.max(0, ...plottedTotals) : undefined,
				},
			},
			exporting: { enabled: false },
		};
		// The props this reads, one by one, rather than `props` itself. `props` is a fresh object on every
		// render, so depending on it gave the options a new identity every time — and Highcharts' React wrapper
		// keys its `chart.update(options, redraw, oneToOne)` on exactly that identity. Every render of anything
		// above the chart therefore re-inited all ~26 series to produce a byte-identical result; opening the side
		// panel did it by flipping `disable`, which these options do not even read.
	}, [
		props.categories,
		props.data,
		props.highlightSecondaryMetric,
		props.mainMetricLabel,
		props.secondaryMetricLabel,
		props.showLegend,
		unit,
		formatNumber,
		hasProposal,
		dragging,
		selectedIds,
		setSelectedIds,
		tooltipId,
		visibleCount,
		zIndex,
	]);

	/**
	 * Resets the navigator's window whenever the portfolios it measures change in number.
	 *
	 * The window deliberately survives a re-render — `chart.update` leaves the live navigator alone, which is
	 * what keeps the range the user picked from being thrown away every time anything above the chart renders
	 * (see `stackTotals`). A *different set of portfolios* is the case where surviving is wrong: the extremes are
	 * indices, so a window over the tail of a long selection stays at, say, `[20, 29]` after a refetch leaves
	 * eight portfolios where there were thirty — the plot then draws an empty stretch past the end while the
	 * strip below it, redrawn from the new data, shows every column there is. Which is the strip and the plot
	 * disagreeing about what the chart is showing.
	 *
	 * `zoomOut` rather than a clamp: a window whose portfolios are gone is not a window the user chose any more.
	 * It runs in a layout effect, and `HighchartsReact`'s own update is one too — in a child, so it has
	 * already applied the new options by the time this fires. Both callers of `MetricComparisonChart` reset
	 * the zoom this way from the outside; here it belongs to the chart, so that no caller can be the one that
	 * forgets.
	 */
	const rowCountRef = useRef(props.data.length);
	useLayoutEffect(() => {
		// Nothing to reset on the first render — the window opens on the whole axis — and resetting it
		// there would only cost the fresh chart a second redraw.
		if (rowCountRef.current !== props.data.length) {
			rowCountRef.current = props.data.length;
			highchartsRef.current?.chart?.zoomOut();
		}
	}, [props.data.length]);

	return (
		<>
			{band && (
				<StackingContext.Consumer>
					{({ zIndex }) =>
						createPortal(
							<div
								className="fixed border bg-slate-500/50 pointer-events-none"
								style={{ zIndex, left: band.left, top: band.top, width: band.width, height: band.height }}
							/>,
							document.body,
						)
					}
				</StackingContext.Consumer>
			)}
			<ChartCardShell
				id={chartId}
				fullHeight={props.chartHeight === "full"}
				title={<ChartTitle>{props.chartTitle}</ChartTitle>}
				midActions={props.midActions}
				actions={props.actions}
			>
				<HighchartsReact
					containerProps={containerProps}
					highcharts={Highcharts}
					options={chartOptions}
					ref={highchartsRef}
				/>
			</ChartCardShell>
		</>
	);
}

export function StackedMetricChartWall({
	status,
	...rest
}: StackedMetricChartProps & { status: ChartWallStatus }): React.JSX.Element {
	return (
		<ChartWallSwitch
			status={status}
			chartTitle={rest.chartTitle}
			midActions={rest.midActions}
			actions={rest.actions}
			chartHeight={rest.chartHeight}
		>
			{() => <StackedMetricChart {...rest} />}
		</ChartWallSwitch>
	);
}
